import React, { useContext } from 'react'

import { twMerge } from 'tailwind-merge'

import { RichTextAreaContext } from '@/components/common/TextArea/RichTextArea/context'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

interface EditorButton extends React.HTMLProps<HTMLButtonElement> {
  isActive: boolean
  type?: 'button' | 'submit' | 'reset'
  buttonFocusClassName?: ({
    isActive,
    isFocused,
  }: {
    isActive: boolean
    isFocused: boolean
  }) => string
}

const EditorButtonTooltip: React.FC<
  React.ComponentProps<typeof TooltipContent>
> = ({ className, children, ...props }) => {
  return (
    <TooltipContent {...props} className={twMerge(className)}>
      {children}
    </TooltipContent>
  )
}

const EditorButtonBase: React.FC<EditorButton> = ({
  children,
  isActive,
  onClick,
  buttonFocusClassName,
  ...props
}) => {
  const { editor, isFocused } = useContext(RichTextAreaContext)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isFocused) {
      editor?.chain().focus('end').run()
    } else {
      onClick?.(event)
    }
  }

  const tooltip = React.Children.toArray(children).find(
    (child): child is React.ReactElement =>
      React.isValidElement(child) && child.type === EditorButtonTooltip,
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild={true}>
        <button
          {...props}
          onClick={handleClick}
          className={twMerge(
            'p-1 rounded-[4px] transition-opacity duration-100',
            'text-primary-500 dark:text-primary-400',
            'transition-colors duration-100',
            isFocused ? '' : 'cursor-text',
            isFocused
              ? isActive
                ? 'bg-primary-200 dark:bg-primary-700 hover:bg-primary-300 dark:hover:bg-primary-600'
                : 'hover:bg-primary-200 dark:hover:bg-primary-700'
              : 'opacity-50 cursor-text',
            buttonFocusClassName?.({ isActive, isFocused }) ?? '',
            props.className,
          )}
          type={props.type ?? 'button'}
        >
          {children}
        </button>
      </TooltipTrigger>
      {tooltip}
    </Tooltip>
  )
}

EditorButtonBase.displayName = 'EditorButton'
EditorButtonTooltip.displayName = 'EditorButton.Tooltip'

const EditorButton = Object.assign(EditorButtonBase, {
  Tooltip: EditorButtonTooltip,
})

export { EditorButton }
